import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { apolloClient } from "../apollo/client";

const BANNER_CLASS = "has-impersonation-banner";

/**
 * Persistent banner shown when the current session is an impersonation.
 *
 * Stamps `has-impersonation-banner` on `<html>` so the CSS --banner-h
 * variable accounts for its height (same pattern as EnvironmentBanner /
 * has-env-banner).
 *
 * The "Stop" flow: POST /auth/stop_impersonation to end the impersonation,
 * then POST /auth/logout to destroy the session entirely. A bare super-admin
 * session must NOT keep using the portal -- superadmins always act through
 * impersonation in the portal.
 */
export function ImpersonationBanner() {
  const { user } = useAuth();
  const [stopping, setStopping] = useState(false);

  const active = Boolean(user?.impersonating);

  useEffect(() => {
    if (active) {
      document.documentElement.classList.add(BANNER_CLASS);
    } else {
      document.documentElement.classList.remove(BANNER_CLASS);
    }
    return () => {
      document.documentElement.classList.remove(BANNER_CLASS);
    };
  }, [active]);

  if (!active || !user?.impersonating) return null;

  const imp = user.impersonating;

  async function handleStop() {
    setStopping(true);
    try {
      // End impersonation first, then logout entirely.
      await fetch("/auth/stop_impersonation", {
        method: "POST",
        credentials: "include",
      });
      await fetch("/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      // Clear Apollo cache so the next session doesn't see stale data.
      try {
        await apolloClient.clearStore();
      } catch {
        // clearStore() cleanup -- never let this break the stop flow.
      }
    } catch {
      // Even if network calls fail, bounce to login.
    }
    window.location.href = "/login";
  }

  return (
    <div
      role="status"
      className="impersonation-banner fixed inset-x-0 z-[49] flex h-8 items-center justify-center gap-3 border-b border-spark/40 bg-spark-s px-4 text-[12px] dark:border-spark/30 dark:bg-spark-s"
    >
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="flex-none text-spark"
      >
        <path d="M12 9v2m0 4h.01M5 19h14a2 2 0 0 0 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.27 16a2 2 0 0 0 1.73 3z" />
      </svg>

      <span className="min-w-0 truncate text-ink-2 dark:text-ink-2">
        <span className="font-semibold text-ink dark:text-ink">Impersonating</span>{" "}
        <span className="font-medium">{imp.email}</span>
        {imp.tenant && (
          <>
            {" "}
            <span className="text-ink-4">({imp.tenant.name})</span>
          </>
        )}
      </span>

      <button
        type="button"
        disabled={stopping}
        onClick={() => void handleStop()}
        className="ml-auto flex-none rounded-md border border-spark/40 bg-paper-hi/60 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-spark transition hover:border-spark hover:bg-paper-hi disabled:cursor-not-allowed disabled:opacity-50 dark:border-spark/30 dark:bg-paper-hi/40 dark:hover:border-spark"
      >
        {stopping ? "Stopping..." : "Stop Impersonation"}
      </button>
    </div>
  );
}
